import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR, { trigger } from 'swr';
import store from 'store';
import { getPurchaseOptions, postShopifyDraftOrder, getShopifyDraftOrder } from './api';

// Attribute casing on these v3 resources isn't guaranteed consistent — same
// tolerance as ../hooks.js's `attr`.
const attr = (resource, key) => {
  const attributes = resource?.attributes || {};
  const kebab = key.replace(/_/g, '-');
  const snake = key.replace(/-/g, '_');
  if (attributes[key] !== undefined) return attributes[key];
  if (attributes[kebab] !== undefined) return attributes[kebab];
  if (attributes[snake] !== undefined) return attributes[snake];
  return undefined;
};

const resourcesFromResponse = response => response?.data?.data || [];
const resourceFromResponse = response => response?.data?.initial?.data || null;

export const purchaseOptionToPlain = resource => ({
  id: resource.id,
  sku: attr(resource, 'sku'),
  kind: attr(resource, 'kind'),
  title: attr(resource, 'title'),
  description: attr(resource, 'description'),
  shortDescription: attr(resource, 'short-description') || null,
  calloutText: attr(resource, 'callout-text') || null,
  badgeLabel: attr(resource, 'badge-label') || null,
  priceCents: Number(attr(resource, 'price-cents')) || 0,
  currency: attr(resource, 'currency') || 'USD',
  group: attr(resource, 'group'),
  quantitySelectable: !!attr(resource, 'quantity-selectable'),
  maxQuantity: Number(attr(resource, 'max-quantity')) || 1,
  upgradeTargetSku: attr(resource, 'upgrade-target-sku') || null,
  targetPlanName: attr(resource, 'target-plan-name') || null,
  // Absent entirely (rather than explicitly false) is the un-filtered shape — treat as eligible.
  eligible: attr(resource, 'eligible') !== false,
  ineligibleReason: attr(resource, 'ineligible-reason') || null,
  variantTitle: attr(resource, 'variant-title') || null,
});

// The groups in the order the API already returns them (guide §3: "upgrades
// first … then add-ons, pages, copies and shipping"). Filtering into these
// buckets only separates them for different controls — it does not reorder
// anything the API returned within a bucket.
export const GROUP_ORDER = ['upgrade', 'feature', 'pages', 'copies', 'shipping'];

export const groupOptions = options => {
  const known = GROUP_ORDER.map(key => ({
    key,
    options: options.filter(option => option.kind === key),
  })).filter(group => group.options.length);

  // Defensive: a group value the client doesn't know about yet is shown rather
  // than silently dropped.
  const unknownKeys = [...new Set(options.map(o => o.group).filter(g => !GROUP_ORDER.includes(g)))];
  const unknown = unknownKeys.map(key => ({
    key,
    options: options.filter(option => option.group === key),
  }));

  return [...known, ...unknown];
};

const PURCHASE_OPTIONS_KEY = (childJournalId, opts) => {
  if (!childJournalId) return null;
  const params = new URLSearchParams();
  if (opts?.group) params.set('group', opts.group);
  if (opts?.includeIneligible) params.set('include_ineligible', 'true');
  const qs = params.toString();
  return `/v3/child_journals/${childJournalId}/purchase_options${qs ? `?${qs}` : ''}`;
};

// Ineligible options are requested too (include_ineligible=true) so the shelf can
// show "already has it" rows greyed out rather than a hole where the member
// expected to find them (guide §3.4) — the design calls for exactly this on the
// Talking Family Tree / Shipping rows.
export const usePurchaseOptions = (childJournalId, opts = { includeIneligible: true }) => {
  const key = PURCHASE_OPTIONS_KEY(childJournalId, opts);
  const { data, error, isValidating } = useSWR(key);
  const options = useMemo(
    () => resourcesFromResponse(data).map(purchaseOptionToPlain),
    [data]
  );
  const revalidate = useCallback(() => key && trigger(key), [key]);
  return { options, groups: groupOptions(options), isLoading: !!key && !data && !error, isValidating, error, revalidate };
};

// Local-only basket: how many of each purchase option is currently selected.
// Kept separate from the journal's persisted entitlements — nothing here is
// bought until checkout succeeds and the draft order is paid.
export const useSelection = options => {
  const optionsById = useMemo(() => Object.fromEntries(options.map(o => [o.id, o])), [options]);
  const [quantities, setQuantities] = useState({});

  // Drop anything that disappeared or went ineligible on the latest fetch (a
  // stale selection surviving a refetch after a 422 would just 422 again).
  useEffect(() => {
    setQuantities(current => {
      let changed = false;
      const next = {};
      Object.entries(current).forEach(([id, qty]) => {
        const option = optionsById[id];
        if (option && option.eligible) {
          const clamped = Math.min(qty, option.maxQuantity || 1);
          next[id] = clamped;
          if (clamped !== qty) changed = true;
        } else {
          changed = true;
        }
      });
      return changed ? next : current;
    });
    // eslint-disable-next-line 
  }, [optionsById]);

  const toggle = useCallback(
    id => {
      const option = optionsById[id];
      if (!option || !option.eligible) return;
      setQuantities(current => {
        if ((current[id] || 0) > 0) {
          const { [id]: _drop, ...rest } = current;
          return rest;
        }
        const next = { ...current, [id]: 1 };
        // The member picks one destination plan, not several at once.
        if (option.group === 'upgrade') {
          Object.keys(next).forEach(otherId => {
            if (otherId !== id && optionsById[otherId]?.group === 'upgrade') delete next[otherId];
          });
        }
        return next;
      });
    },
    [optionsById]
  );

  const setQuantity = useCallback(
    (id, quantity) => {
      const option = optionsById[id];
      if (!option || !option.eligible) return;
      const clamped = Math.max(0, Math.min(Number(quantity) || 0, option.maxQuantity || 1));
      setQuantities(current => {
        if (clamped <= 0) {
          const { [id]: _drop, ...rest } = current;
          return rest;
        }
        return { ...current, [id]: clamped };
      });
    },
    [optionsById]
  );

  const clear = useCallback(() => setQuantities({}), []);

  // One line per selected option — `quantities` is keyed by id, so there is no
  // way to double-list the same option. `quantity` is omitted for the default of
  // one, per the guide. Capped at 20 lines (the API's own ceiling).
  const lineItems = useMemo(
    () =>
      Object.entries(quantities)
        .filter(([, qty]) => qty > 0)
        .slice(0, 20)
        .map(([id, quantity]) => (quantity > 1 ? { variant_id: id, quantity } : { variant_id: id })),
    [quantities]
  );

  const subtotalCents = useMemo(
    () =>
      Object.entries(quantities).reduce((sum, [id, qty]) => {
        const option = optionsById[id];
        return option ? sum + option.priceCents * qty : sum;
      }, 0),
    [quantities, optionsById]
  );

  return {
    quantities,
    isSelected: id => (quantities[id] || 0) > 0,
    quantityOf: id => quantities[id] || 0,
    toggle,
    setQuantity,
    clear,
    lineItems,
    subtotalCents,
    hasSelection: lineItems.length > 0,
  };
};

export const draftOrderToPlain = resource => ({
  id: resource.id,
  status: attr(resource, 'status'),
  invoiceUrl: attr(resource, 'invoice-url'),
  name: attr(resource, 'name'),
  currency: attr(resource, 'currency'),
  subtotalPrice: attr(resource, 'subtotal-price'),
  totalPrice: attr(resource, 'total-price'),
  childJournalId: attr(resource, 'child-journal-id'),
  shopifyOrderId: attr(resource, 'shopify-order-id'),
  completedAt: attr(resource, 'completed-at'),
  errorMessage: attr(resource, 'error-message'),
  lineItems: attr(resource, 'line-items') || [],
});

// A draft order started but not yet resolved is remembered per-journal so a
// reload (or returning to the app later, per the API guide's §6.1 "survives a
// closed tab") can resume polling it instead of losing track of the purchase.
const pendingDraftOrderKey = childJournalId => `purchase_pending_draft_order_${childJournalId}`;

export const getPendingDraftOrder = childJournalId => store.get(pendingDraftOrderKey(childJournalId)) || null;

const rememberPendingDraftOrder = (childJournalId, draftOrder) =>
  store.set(pendingDraftOrderKey(childJournalId), { id: draftOrder.id, invoiceUrl: draftOrder.invoiceUrl });

export const forgetPendingDraftOrder = childJournalId => store.remove(pendingDraftOrderKey(childJournalId));

// Builds and submits the draft order for the current selection. One in flight at
// a time — the ref (not state) guards a double tap racing ahead of the first
// render triggered by `isCreating`.
export const useCreateDraftOrder = childJournalId => {
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState(null);
  const submittingRef = useRef(false);

  const createDraftOrder = useCallback(
    async lineItems => {
      if (submittingRef.current || !lineItems.length) return { ok: false };
      submittingRef.current = true;
      setIsCreating(true);
      setError(null);
      try {
        const response = await postShopifyDraftOrder(childJournalId, lineItems);
        const resource = resourceFromResponse(response);
        const draftOrder = resource ? draftOrderToPlain(resource) : null;
        if (draftOrder) rememberPendingDraftOrder(childJournalId, draftOrder);
        return { ok: true, draftOrder };
      } catch (err) {
        setError(err);
        return { ok: false, error: err };
      } finally {
        submittingRef.current = false;
        setIsCreating(false);
      }
    },
    [childJournalId]
  );

  return { createDraftOrder, isCreating, error, clearError: () => setError(null) };
};

const ACTIVE_POLL_INTERVAL_MS = 3000; // "every 2-3 seconds" per the API guide §6.1
const ACTIVE_POLL_WINDOW_MS = 20000; // then back off and let the member refresh manually

// Polls GET /v3/shopify_draft_orders/:id while a purchase is unresolved.
// `pending` and `open` both mean "not yet" (guide §6: "treat pending as open");
// `completed` / `failed` / `unresolved` are terminal. Re-checks immediately on
// foreground (visibility/focus) — the "existing app lifecycle" hook for the
// member coming back from paying — and backs off to manual refresh after ~20s so
// this never polls indefinitely.
export const useDraftOrderPolling = (draftOrderId, { onCompleted } = {}) => {
  const [state, setState] = useState({
    status: draftOrderId ? 'checking' : 'idle',
    draftOrder: null,
    error: null,
    backedOff: false,
  });

  const stoppedRef = useRef(false);
  const intervalRef = useRef(null);
  const backoffRef = useRef(null);
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  const stopTimers = () => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (backoffRef.current) clearTimeout(backoffRef.current);
    intervalRef.current = null;
    backoffRef.current = null;
  };

  const checkOnceRef = useRef(() => {});
  checkOnceRef.current = async () => {
    if (!draftOrderId || stoppedRef.current) return;
    try {
      const response = await getShopifyDraftOrder(draftOrderId);
      if (stoppedRef.current) return;
      const resource = resourceFromResponse(response);
      const draftOrder = resource ? draftOrderToPlain(resource) : null;
      const status = draftOrder?.status;

      if (status === 'completed' || status === 'failed' || status === 'unresolved') {
        stoppedRef.current = true;
        stopTimers();
        setState({ status, draftOrder, error: null, backedOff: false });
        if (status === 'completed') onCompletedRef.current?.(draftOrder);
        return;
      }
      // 'pending' / 'open' — still waiting on Shopify / the webhook.
      setState(current => ({ ...current, status: 'waiting', draftOrder, error: null }));
    } catch (err) {
      if (stoppedRef.current) return;
      setState(current => ({ ...current, error: err }));
    }
  };

  useEffect(() => {
    if (!draftOrderId) {
      setState({ status: 'idle', draftOrder: null, error: null, backedOff: false });
      return undefined;
    }

    stoppedRef.current = false;
    setState({ status: 'checking', draftOrder: null, error: null, backedOff: false });

    checkOnceRef.current();
    intervalRef.current = setInterval(() => checkOnceRef.current(), ACTIVE_POLL_INTERVAL_MS);
    backoffRef.current = setTimeout(() => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = null;
      setState(current =>
        current.status === 'waiting' || current.status === 'checking'
          ? { ...current, backedOff: true }
          : current
      );
    }, ACTIVE_POLL_WINDOW_MS);

    const handleForeground = () => {
      if (document.visibilityState === 'visible') checkOnceRef.current();
    };
    document.addEventListener('visibilitychange', handleForeground);
    window.addEventListener('focus', handleForeground);

    return () => {
      stoppedRef.current = true;
      stopTimers();
      document.removeEventListener('visibilitychange', handleForeground);
      window.removeEventListener('focus', handleForeground);
    };
  }, [draftOrderId]);

  const checkNow = useCallback(() => checkOnceRef.current(), []);

  return { ...state, checkNow };
};
